import { Suspense } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import {
	Layout,
	Menu,
	Row,
	theme,
	Dropdown,
	Col,
	Button,
	Image,
	Spin,
	Avatar,
	Result,
} from "antd";
import { CaretDownOutlined, UserOutlined } from "@ant-design/icons";
import Logo from "::assets/logo/taleemy-logo.png";
import useDefaultLayout from "./useDefaultLayout";
import { useAuth } from "@/contexts";
import { ROUTES } from "@/consts";
import useAdminProfile from "@/services/auth/useAdminProfile";
import { AppLoading } from "@/components/core";

const { Header, Content, Sider } = Layout;
const SIDEBAR_WIDTH = 220;

const DefaultLayout = () => {
	const {
		token: { colorBgContainer },
	} = theme.useToken();
	const {
		user,
		error: profileError,
		refetch: refetchProfile,
	} = useAdminProfile();

	const {
		items,
		pathnameWithParams,
		dropdownItems,
		expandedKeys,
		handleLogout,
		logoutLoading,
	} = useDefaultLayout();

	const location = useLocation();
	const {
		state: { token },
	} = useAuth();
	const userName = user?.name?.trim() || "Account";
	const userInitial = userName.charAt(0).toUpperCase();

	if (!token && location.pathname !== ROUTES.Login) {
		return <Navigate to={ROUTES.Login} />;
	}

	if (!user && profileError?.networkError) {
		return (
			<Layout style={{ minHeight: "100vh", justifyContent: "center" }}>
				<Result
					status="error"
					title="Couldn't load your profile"
					subTitle={profileError.message}
					extra={
						<Button type="primary" onClick={() => refetchProfile()}>
							Retry
						</Button>
					}
				/>
			</Layout>
		);
	}

	if (!user) {
		return <AppLoading />;
	}

	return (
		<Spin spinning={logoutLoading}>
			<Layout style={{ minHeight: "100vh" }} hasSider>
				<Sider
					className="app-sidebar"
					theme="light"
					width={SIDEBAR_WIDTH}
					collapsedWidth={SIDEBAR_WIDTH}
					style={{
						paddingTop: 20,
						overflow: "auto",
						height: "100vh",
						position: "fixed",
						left: 0,
						top: 0,
						bottom: 0,
						flexDirection: "column",
					}}
				>
					<Row
						justify="center"
						style={{ flexShrink: 0, padding: "0 16px 20px 16px" }}
					>
						<Image
							src={Logo}
							preview={false}
							width="80%"
							alt="Ta3leemy Logo"
							placeholder="Ta3leemy Logo"
						/>
					</Row>
					<div
						style={{
							flex: 1,
							display: "flex",
							flexDirection: "column",
							paddingBottom: 16,
						}}
					>
						<Menu
							defaultSelectedKeys={[pathnameWithParams]}
							defaultOpenKeys={expandedKeys}
							mode="inline"
							items={items}
							style={{
								flex: 1,
								borderRight: 0,
								height: "100%",
							}}
						/>
					</div>
				</Sider>

				<Layout style={{ marginLeft: SIDEBAR_WIDTH }}>
					<Header
						className="app-header"
						style={{ background: colorBgContainer }}
					>
						<Row align="middle" justify="end" style={{ height: "100%" }}>
							<Col style={{ display: "flex", alignItems: "center" }}>
								<Dropdown
									menu={{ items: dropdownItems, onClick: handleLogout }}
									trigger={["click"]}
								>
									<Button
										aria-label="Open profile menu"
										className="app-profile-trigger"
									>
										<Avatar
											className="app-profile-trigger__avatar"
											icon={!userInitial ? <UserOutlined /> : undefined}
											size={30}
										>
											{userInitial}
										</Avatar>
										<span className="app-profile-trigger__name">
											{userName}
										</span>
										<span
											aria-hidden="true"
											className="app-profile-trigger__chevron"
										>
											<CaretDownOutlined />
										</span>
									</Button>
								</Dropdown>
							</Col>
						</Row>
					</Header>
					<Content style={{ padding: 30 }}>
						{/* TODO: having a fancy loading state for lazy loaded pages */}
						<Suspense fallback={<div>Loading...</div>}>
							<Outlet />
						</Suspense>
					</Content>
				</Layout>
			</Layout>
		</Spin>
	);
};

export default DefaultLayout;
